import { cloneElement } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import { TickIcon, WarningIcon } from "@eisox/icons";

import { Card, type CardProps } from "../Card";
import { Circle } from "../Circle";
import { cn } from "../index";

const alertVariants = cva("gap-x-2.5 p-2.5 flex items-center text-base whitespace-pre-line", {
  variants: {
    severity: {
      error: "bg-primary-red text-primary-white",
      warning: "bg-primary-warning",
      help: "bg-primary-gray",
      success: "bg-primary-green text-primary-white",
    },
  },
  defaultVariants: {
    severity: "warning",
  },
});

const logoVariants = cva("w-6 h-6 shrink-0");

export interface AlertProps extends CardProps, VariantProps<typeof alertVariants> {}

export const Alert: React.FC<AlertProps> = ({ severity = "warning", className, children, ...props }) => {
  const ENUM_SEVERITY = {
    error: <WarningIcon />,
    warning: <WarningIcon />,
    help: (
      <Circle.Root size={24} color="darkGray" backgroundColor="white">
        ?
      </Circle.Root>
    ),
    success: (
      <Circle.Root size={24} color="green" backgroundColor="white">
        <TickIcon />
      </Circle.Root>
    ),
  };

  return (
    <Card {...props} className={cn(alertVariants({ severity, className }))}>
      {cloneElement(ENUM_SEVERITY[severity ?? "warning"], {
        className: cn(logoVariants(), ENUM_SEVERITY[severity ?? "warning"].props.className),
      })}
      {children}
    </Card>
  );
};
